import { apiClient } from "@/services/apiClient";

/**
 * Admin-facing service for managing the list of email addresses that
 * may receive portal magic links for a given client.
 *
 * Endpoints:
 *   GET    /client/:clientId/emails
 *   POST   /client/:clientId/emails
 *   PATCH  /client/:clientId/emails/:emailId
 *   DELETE /client/:clientId/emails/:emailId
 */

export type ClientEmailRole = "client" | "creator";

export interface ClientEmail {
  id: string;
  clientId: string;
  email: string;
  label: string | null;
  personId: string | null;
  /** The person's global name (TD-086). */
  name: string | null;
  role: ClientEmailRole;
  verifiedAt: string | null;
  createdAt: string;
}

export interface AddClientEmailBody {
  email: string;
  name?: string;
  role?: ClientEmailRole;
}

export interface AddClientEmailResponse {
  email: ClientEmail;
  /** Backend reports whether the welcome magic link was actually sent. */
  emailSent: boolean;
  /** False when a name was sent but the person keeps another one: they are
   * tied to another agency (TD-086). */
  nameApplied: boolean;
}

export interface UpdateClientEmailRoleResponse {
  email: ClientEmail;
}

interface ListResponse {
  success: boolean;
  emails: ClientEmail[];
}

export const clientEmailsService = {
  async list(clientId: string): Promise<ClientEmail[]> {
    const res = await apiClient.get<ListResponse>(`/client/${clientId}/emails`);
    return res.emails ?? [];
  },

  async add(
    clientId: string,
    body: AddClientEmailBody,
  ): Promise<AddClientEmailResponse> {
    const payload: AddClientEmailBody = { email: body.email };
    if (body.name && body.name.trim()) {
      payload.name = body.name.trim();
    }
    if (body.role) {
      payload.role = body.role;
    }
    return apiClient.post<AddClientEmailResponse>(
      `/client/${clientId}/emails`,
      payload,
    );
  },

  async updateRole(
    clientId: string,
    emailId: string,
    role: ClientEmailRole,
  ): Promise<UpdateClientEmailRoleResponse> {
    return apiClient.patch<UpdateClientEmailRoleResponse>(
      `/client/${clientId}/emails/${emailId}`,
      { role },
    );
  },

  async remove(clientId: string, emailId: string): Promise<void> {
    await apiClient.delete(`/client/${clientId}/emails/${emailId}`);
  },
};
